import React, { RefObject, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FieldColorNames } from 'Hooks/DocumentFields/useDocumentFieldColor';
import { DatePipeOptions } from 'Interfaces/Common';
import { InteractModes, Signer } from 'Interfaces/Document';
import {
  DocumentField,
  DocumentFieldsCRUDMeta,
  DocumentFieldTypes,
  DocumentFieldUpdatePayload,
} from 'Interfaces/DocumentFields';
import { RequisiteType } from 'Interfaces/Requisite';
import Interact from 'Services/Interact';
import { selectRequisite } from 'Utils/selectors';

import {
  CheckboxField,
  DateField,
  NameField,
  RequisiteField,
  TextFieldSign,
} from '../FieldTypes';

interface FieldTriggerProps {
  containerRef: RefObject<HTMLDivElement>;
  imgRef: RefObject<HTMLImageElement>;
  textFieldRef: RefObject<HTMLTextAreaElement>;
  field: DocumentField;
  fieldColor: FieldColorNames;
  signer: Signer;
  requisiteId?: string | null;
  textStyles: { fontSize?: string | number; fontFamily?: string };
  isDragging: boolean;
  inFocus?: boolean;
  onUpdateDocumentField: (
    payload: Omit<DocumentFieldUpdatePayload, 'id'>,
    meta?: DocumentFieldsCRUDMeta,
  ) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  interactMode?: InteractModes;
  disabled?: boolean;
  getRelatedPage: () => HTMLElement;
}

function FieldTrigger({
  containerRef,
  imgRef,
  textFieldRef,
  field,
  fieldColor,
  signer,
  textStyles,
  interactMode,
  requisiteId,
  isDragging,
  onUpdateDocumentField,
  onFocus,
  onBlur,
  disabled,
  getRelatedPage,
}: FieldTriggerProps) {
  const {
    id,
    width,
    dateFormat,
    type,
    checked,
    height,
    fixedFontSize,
    pageNumber,
    text,
    placeholder,
    createType,
  } = field;

  const isPreparer = !!signer?.isPreparer;

  const requisite: ReturnType<typeof selectRequisite> = useSelector(state =>
    selectRequisite(state, { requisiteId: requisiteId || '' }),
  );

  const updateDocumentRequisiteField = useCallback(
    (payload: Omit<DocumentFieldUpdatePayload, 'id'>, meta?: DocumentFieldsCRUDMeta) => {
      if (!containerRef.current) return;
      const { x: coordinateX } = Interact.getNodeCoords(containerRef.current);
      onUpdateDocumentField(
        {
          coordinateX: coordinateX - getRelatedPage().offsetLeft,
          pageNumber,
          ...payload,
        },
        meta,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, onUpdateDocumentField],
  );

  switch (type) {
    case DocumentFieldTypes.Name: {
      return (
        <NameField
          value={text}
          signer={signer}
          onFocus={onFocus}
          onBlur={onBlur}
          onFieldDataChange={onUpdateDocumentField}
          style={textStyles}
          fieldColor={fieldColor}
          isInsertable={isPreparer}
          disabled={isDragging || disabled}
        />
      );
    }
    case DocumentFieldTypes.Signature:
    case DocumentFieldTypes.Initials: {
      return (
        <RequisiteField
          disabled={isDragging || disabled}
          requisiteType={
            type === DocumentFieldTypes.Signature
              ? RequisiteType.SIGN
              : RequisiteType.INITIAL
          }
          fieldColor={fieldColor}
          signer={signer}
          requisite={requisite}
          createType={createType}
          imgRef={imgRef}
          updateField={
            interactMode === InteractModes.PREPARING
              ? updateDocumentRequisiteField
              : undefined
          }
        />
      );
    }
    case DocumentFieldTypes.Checkbox: {
      return (
        <CheckboxField
          fieldColor={fieldColor}
          disabled={isDragging || disabled}
          checked={!!checked}
        />
      );
    }
    case DocumentFieldTypes.Text: {
      return (
        <TextFieldSign
          ref={textFieldRef}
          onFieldDataChange={onUpdateDocumentField}
          isFixedFontSize={fixedFontSize}
          value={text}
          placeholder={placeholder}
          signer={signer}
          onFocus={onFocus}
          onBlur={onBlur}
          style={{ ...textStyles, width, height }}
          fieldColor={fieldColor}
          disabled={
            isDragging ||
            disabled ||
            (interactMode === InteractModes.PREPARING && !isPreparer)
          }
        />
      );
    }
    case DocumentFieldTypes.Date: {
      const datePipeOptions: DatePipeOptions =
        interactMode === InteractModes.SIGNING
          ? { isSameOrFuture: true }
          : { minYY: 20, minYYYY: 2020 };

      return (
        <DateField
          value={text}
          onFocus={onFocus}
          onBlur={onBlur}
          onFieldDataChange={onUpdateDocumentField}
          style={textStyles}
          fieldColor={fieldColor}
          dateFormat={dateFormat as NonNullable<DocumentField['dateFormat']>}
          isInsertable={isPreparer}
          disabled={isDragging || disabled}
          datePipeOptions={datePipeOptions}
        />
      );
    }
    default: {
      return null;
    }
  }
}

export default FieldTrigger;
